import { RolesColumns } from "./columns";

import { z } from "zod";

// Extend ZodType to support childOf metadata for UI grouping
declare module "zod" {
  interface ZodType<Output = any, Def = z.ZodTypeDef, Input = Output> {
    childOf(parent: string): this;
  }
}

(z.ZodType as any).prototype.childOf = function (parent: string) {
  this._def.ui = { ...(this._def.ui ?? {}), childOf: parent };
  return this;
};

export const CRUDSchema = z.object({
  create: z.boolean().default(false),
  read: z.boolean().default(false),
  update: z.boolean().default(false),
  delete: z.boolean().default(false),
});

export const CommentSchema = z.object({
  comments: z
    .object({
      create: z.boolean().default(false),
      read: z.boolean().default(false),
      modify: z.boolean().default(false),
    })
    .default({}),
});

export const PermissionsSchema = z
  .object({
    admin_portal: z.boolean().default(false).describe("Access to admin portal and advanced features"),
    clients: CRUDSchema.default({}).describe("Manage client accounts and information"),
    courses: CRUDSchema.default({}).describe("Manage courses for users to earn skills and certs"),
    projects: CRUDSchema.default({}).describe("Manage projects and assignments"),
    markets: CRUDSchema.default({}).describe("Manage markets"),
    shifts: CRUDSchema.default({}).describe("Manage work shifts and scheduling"),
    shift_users: CRUDSchema.default({}).describe("Manage shift user assignments").childOf("shifts"),
    shift_positions: CRUDSchema.default({}).describe("Manage shift positions").childOf("shifts"),
    time_cards: CRUDSchema.default({}).describe("Manage employee time tracking"),
    roles: CRUDSchema.default({}).describe("Manage roles and permissions"),
    invoices: CRUDSchema.default({}).describe("Handle billing and invoices"),
    estimates: CRUDSchema.default({}).describe("Create and manage estimates"),
    expenses: CRUDSchema.default({}).describe("Track and manage expenses"),
    payroll: CRUDSchema.default({}).describe(" Create and issue payments"),
    users: CRUDSchema.default({ read: true }).describe("Manage user accounts and profiles"),
    user_abilities: CRUDSchema.default({ read: true })
      .describe("Manage user abilities and certifications")
      .childOf("users"),
    user_positions: CRUDSchema.default({ read: true }).describe("Manage user positions").childOf("users"),
    user_restrictions: CRUDSchema.default({}).describe("Manage user restrictions and limitations").childOf("users"),
    user_private: CRUDSchema.default({}).describe("Access private user information").childOf("users"),
    user_pay: CRUDSchema.default({}).describe("Access user pay information").childOf("users"),
    user_reviews: CRUDSchema.default({}).describe("Manage user performance reviews").childOf("users"),
    user_emergency_contacts: CRUDSchema.default({ read: true })
      .describe("Manage emergency contact information")
      .childOf("users"),
    contacts: CRUDSchema.default({}).describe("Manage contact information"),
    incidents: CRUDSchema.default({}).describe("Manage safety incidents and reports"),
    investigations: CRUDSchema.default({}).describe("Handle incident investigations"),
    applications: CRUDSchema.default({}).describe("Manage job applications"),
    abilities: CRUDSchema.default({ read: true }).describe("Manage ability types"),
    restrictions: CRUDSchema.default({ read: true }).describe("Manage restriction types"),
    billing_items: CRUDSchema.default({}).describe("Manage billing items and rates"),
    locations: CRUDSchema.default({ read: true }).describe("Manage work locations"),
    unions: CRUDSchema.default({ read: true }).describe("Manage unions"),
    dress_codes: CRUDSchema.default({ read: true }).describe("Manage dress code requirements"),
    positions: CRUDSchema.default({ read: true }).describe("Manage job positions"),
    incident_types: CRUDSchema.default({ read: true }).describe("Manage incident types"),
    terms: CRUDSchema.default({ read: true }).describe("Manage terms and conditions"),
    tenant: z
      .object({ update: z.boolean().default(false) })
      .default({})
      .describe("Update tenant configuration"),
    phones: z
      .array(
        z.object({
          show_number: z.boolean().default(true),
          alias: z.string().optional(),
          number: z.string(),
        }),
      )
      .default([]),
  })
  .default({});

export type PermissionsType = z.infer<typeof PermissionsSchema>;

export type RoleType = Omit<RolesColumns, "meta_data"> & {
  meta_data: {
    permissions: PermissionsType;
    accept_shifts?: boolean;
    priority?: number;
  };
  roles_users?: { user_id: string; tenant_id: string }[];
};
